import React, { useCallback, useState } from "react";
import {
  Chip,
  InputLabel,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseRounded from "@mui/icons-material/CloseRounded";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useDropzone } from "react-dropzone";
import type { FileRejection, FileError } from "react-dropzone";

type FormikFileField = {
  name: string;
  value?: File | File[] | FileList | null | "";
};

interface RpDropzoneProps {
  field: FormikFileField;
  form: {
    errors: Record<string, React.ReactNode>;
    setFieldValue(_field: string, _value: File | File[] | ""): void;
    isValid: boolean;
    touched: Record<string, boolean | undefined>;
  };
  label?: React.ReactNode;
  accept?: string | string[];
  className?: string;
  downloadTemplate?: React.ReactNode;
  multiple?: boolean;
  maxRequiredSize?: number;
  required?: boolean;
}

const isFile = (value: FormikFileField["value"]): value is File =>
  Boolean(value && value instanceof File);

const DropzoneWrapper = styled("div")(({ theme }) => ({
  "& .dropzone": {
    border: `2px dashed ${theme?.palette?.primary?.main}`,
    borderRadius: "var(--radius-control)",
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    transition: "border 0.3s ease-in-out",
    backgroundColor: theme.palette.background.paper,

    "&:hover": {
      borderColor: theme.palette.primary.dark,
    },

    "&.error": {
      borderColor: theme?.palette?.error?.main,
    },
  },

  "& .icon": {
    fontSize: 48,
    marginBottom: theme.spacing(1),
    color: theme?.palette?.primary?.main,
  },

  "& .fileInfo": {
    marginTop: theme.spacing(2),
  },

  "& .errorText": {
    color: theme?.palette?.error?.main,
    marginTop: theme.spacing(1),
    fontSize: "0.75rem",
  },

  "& .fileTypeNote": {
    marginTop: theme.spacing(1),
    fontSize: "12px",
    color:
      (theme.vars || theme).color?.["text-secondary"] ||
      (theme.vars || theme).palette.text.secondary,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
  },

  "& .chipContainer": {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
  },

  "& .chip": {
    backgroundColor:
      (theme.vars || theme).palette.primary.light ||
      (theme.vars || theme).palette.primary.light,
    color: theme?.palette?.primary?.main,
    height: "24px",
    fontSize: "11px",
  },

  "& .loadingContainer": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },

  "& .loadingText": {
    marginLeft: theme.spacing(1),
    color: theme?.palette?.primary?.main,
  },
}));

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export const RpDropzone = ({
  field,
  form: { errors, setFieldValue, isValid, touched },
  label,
  accept,
  className = "",
  downloadTemplate,
  multiple,
  maxRequiredSize = 150 * 1024 * 1024,
  ...rest
}: RpDropzoneProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const maxSize = maxRequiredSize; // 150 MB
  const remove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setFieldValue(field.name, "");
    },
    [field.name, setFieldValue]
  );

  const fileTypes = accept
    ? (Array.isArray(accept) ? accept : accept.split(",")).map((type) =>
        type.trim()
      )
    : null;

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setIsLoading(true);
      const files = multiple ? acceptedFiles : acceptedFiles[0];
      // Simulate file processing delay
      setTimeout(() => {
        setFieldValue(field.name, files);
        setIsLoading(false);
      }, 1000);
    },
    [field.name, setFieldValue, multiple]
  );

  // Custom file validator
  const validateFile = useCallback(
    (file: File): FileError | null => {
      if (!fileTypes) return null;

      // Check file size
      if (file.size > maxSize) {
        return {
          code: "file-too-large",
          message: `File is too large. Maximum file size is ${formatFileSize(maxSize)}`,
        };
      }

      const fileExtension = file.name
        ? `.${file.name.split(".").pop().toLowerCase()}`
        : "";
      const isValidExtension = fileTypes.some(
        (type) =>
          type &&
          fileExtension &&
          type.toLowerCase() === fileExtension.toLowerCase()
      );

      if (!isValidExtension) {
        return {
          code: "file-invalid-type",
          message: `File type must be ${fileTypes.join(", ")}`,
        };
      }

      return null;
    },
    [fileTypes, maxSize]
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      onDrop,
      maxSize: maxSize,
      multiple: multiple || false,
      validator: validateFile,
      // Don't set accept here
    });

  // Custom error message handling
  const getErrorMessage = (
    fileRejections: readonly FileRejection[],
    formError: React.ReactNode
  ) => {
    if (fileRejections?.length > 0) {
      const rejection = fileRejections[0];
      if (rejection.errors[0].code === "file-too-large") {
        return `File size (${formatFileSize(rejection.file.size)}) exceeds maximum limit of ${formatFileSize(maxSize)}`;
      }
      return rejection.errors[0].message;
    }
    return formError;
  };

  const hasError =
    (touched[field.name] && !isValid && errors[field.name]) ||
    fileRejections.length > 0;

  const errorMessage = getErrorMessage(fileRejections, errors[field.name]);

  return (
    <DropzoneWrapper>
      <div className={`inputWrapper ${className}`}>
        <div className="d-md-flex align-items-start mb-2">
          <InputLabel
            shrink
            className="m-0"
            margin="dense"
            disableAnimation
            sx={{
              fontWeight: "bold",
              color: "var(--neutral-700)",
            }}
            required={rest?.required}
          >
            {label}
          </InputLabel>
          <div className="d-flex justify-content-end flex-grow-1">
            {downloadTemplate}
          </div>
        </div>
        <Paper
          {...getRootProps()}
          elevation={0}
          className={`dropzone ${hasError ? "error" : ""}`}
        >
          <input
            {...getInputProps({
              accept: fileTypes ? fileTypes.join(",") : undefined,
            })}
          />
          <CloudUploadIcon className="icon" />

          {isLoading ? (
            <div className="loadingContainer">
              <CircularProgress size={24} color="primary" />
              <Typography variant="body2" className="loadingText">
                Processing file...
              </Typography>
            </div>
          ) : field && field.value ? (
            <div className="fileInfo">
              <Typography variant="body2">
                <CloseRounded
                  onClick={remove}
                  className="mx-1 mr-2 text-danger fw-b shadow"
                  titleAccess="Remove"
                />
                Selected:{" "}
                {multiple ? (
                  Array.from(field.value as File[] | FileList).map(
                    (file, index) => (
                      <div key={index}>
                        {file.name} ({formatFileSize(file.size)})
                      </div>
                    )
                  )
                ) : isFile(field.value) ? (
                  <>
                    {field.value.name} ({formatFileSize(field.value.size)})
                  </>
                ) : (
                  Array.from(field.value as FileList).map((file, index) => (
                    <div key={index}>
                      {file.name} ({formatFileSize(file.size)})
                    </div>
                  ))
                )}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Maximum file size: {formatFileSize(maxSize)}
              </Typography>
            </div>
          ) : (
            <div>
              <Typography variant="body1">
                {isDragActive
                  ? "Drop the files here"
                  : `Drag and drop ${multiple ? "files" : "a file"} here, or click to select`}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Maximum file size: {formatFileSize(maxSize)}
              </Typography>
            </div>
          )}

          {hasError && (
            <Typography className="errorText">{errorMessage}</Typography>
          )}
        </Paper>
        {fileTypes && (
          <div className="fileTypeNote">
            <Typography variant="caption" className="label">
              Accepted file types:
            </Typography>
            <div className="chipContainer">
              {fileTypes.map((type) => (
                <Chip key={type} label={type} size="small" className="chip" />
              ))}
            </div>
          </div>
        )}
      </div>
    </DropzoneWrapper>
  );
};
